import { useScan } from './ScanLayout';
import { PageHeader, Section, Banner, KV, CodeBlock } from '../components/Section';
import { ModuleRemediation } from '../components/Remediation';

const HEADERS: { raw: string; label: string; key: string; required: boolean }[] = [
  { raw: 'strict-transport-security', label: 'HSTS',                     key: 'hsts',                   required: true },
  { raw: 'content-security-policy',   label: 'CSP',                      key: 'csp',                    required: true },
  { raw: 'x-frame-options',           label: 'X-Frame-Options',          key: 'x_frame_options',        required: true },
  { raw: 'x-content-type-options',    label: 'X-Content-Type-Options',   key: 'x_content_type_options', required: true },
  { raw: 'referrer-policy',           label: 'Referrer-Policy',          key: 'referrer_policy',        required: false },
  { raw: 'permissions-policy',        label: 'Permissions-Policy',       key: 'permissions_policy',     required: false },
  { raw: 'x-xss-protection',          label: 'X-XSS-Protection',         key: 'x_xss_protection',       required: false }
];

function hstsMaxAge(v?: string): number | null {
  if (!v) return null;
  const m = v.match(/max-age\s*=\s*"?(\d+)"?/i);
  return m ? Number(m[1]) : null;
}

// A header can be present yet ineffective (report-only CSP, max-age=0 HSTS).
// Reflect that as a warning rather than a green "PRESENT".
function headerPill(key: string, value: string | undefined, required: boolean, cspReportOnly?: boolean): {
  cls: string; label: string; note?: string;
} {
  if (!value) return { cls: required ? 'pill-warn' : 'pill-mute', label: 'MISSING' };
  if (key === 'csp' && cspReportOnly) {
    return { cls: 'pill-warn', label: 'REPORT-ONLY', note: 'Report-only mode logs violations but enforces nothing. Serve Content-Security-Policy (not -Report-Only) to actually block.' };
  }
  if (key === 'hsts') {
    const ma = hstsMaxAge(value);
    if (ma === 0) return { cls: 'pill-warn', label: 'max-age=0', note: 'max-age=0 disables HSTS. Set a positive max-age (e.g. 31536000).' };
    if (ma !== null && ma < 86400) return { cls: 'pill-warn', label: 'WEAK', note: 'max-age is under a day — too short to protect returning visitors. Use 15552000+ (180 days).' };
  }
  return { cls: 'pill-ok', label: 'PRESENT' };
}

export function WebPage() {
  const scan = useScan();
  const m = scan.modules.web;
  if (!m) return (
    <>
      <PageHeader title="Web Security" subtitle="Module result not available." />
      <Section accent="amber"><Banner tone="warn" title="Web analyzer didn't return a result" /></Section>
    </>
  );

  return (
    <>
      <PageHeader
        title="Web Security Analysis"
        subtitle="HTTP security headers, redirect policy, and transport-layer hardening"
      />

      <Section title="Reachability" accent="blue">
        <div className="grid gap-4 sm:grid-cols-3">
          <KV k="Final URL" v={
            <span className="break-all">
              {m.url}
              {m.redirected_to && (
                <span className="ml-2 inline-block rounded-sm bg-sev-med/15 px-1.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wide text-sev-med">
                  redirected
                </span>
              )}
            </span>
          } />
          <KV k="Status" v={m.status ?? '—'} />
          <KV k="Server" v={m.headers.server ?? '—'} />
        </div>
      </Section>

      <Section title="HTTP Security Headers" accent="blue">
        <div className="space-y-2">
          {HEADERS.map(h => {
            const value = (m.headers as Record<string, string | undefined>)[h.key];
            const pill = headerPill(h.key, value, h.required, m.headers.csp_report_only);
            return (
              <div key={h.raw} className="rounded-lg border border-line bg-panel-2/40 p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <span className="font-medium text-ink">{h.label}</span>
                    {h.required && (
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-ink-3">required</span>
                    )}
                  </div>
                  <span className={`pill ${pill.cls}`}>{pill.label}</span>
                </div>
                {value && (
                  <div className="mt-2.5">
                    <CodeBlock>{String(value)}</CodeBlock>
                  </div>
                )}
                {pill.note && <p className="mt-2 text-xs text-amber-700">{pill.note}</p>}
              </div>
            );
          })}
        </div>
      </Section>

      {m.missing.length > 0 && (
        <Section title="Missing Headers" accent="amber"
          status={{ label: `${m.missing.length} MISSING`, tone: 'warn' }}>
          <Banner tone="warn" title="Recommended headers not set">
            Add these to harden the site against XSS, clickjacking, downgrade attacks
            and content-type sniffing.
          </Banner>
          <ul className="mt-3 grid gap-1.5 sm:grid-cols-2">
            {m.missing.map(h => (
              <li key={h} className="rounded-md bg-sev-med/12 px-3 py-1.5 font-mono text-xs text-ink">
                {h}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {scan.remediations.some(r => r.module === 'web') && (
        <Section title="How to fix" accent="blue">
          <ModuleRemediation remediations={scan.remediations} modules={['web']} />
        </Section>
      )}
    </>
  );
}
